import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  Users,
  Search,
  Download,
  ShieldCheck,
  ShieldAlert,
  Mail,
  Phone,
  KeyRound,
  KeyIcon,
  Building2,
  Loader2,
  RefreshCw,
  Eye,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminHostsTab = () => {
  const [hosts, setHosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [panFilter, setPanFilter] = useState('all');
  const [selectedHost, setSelectedHost] = useState(null);
  const [showProperties, setShowProperties] = useState(false);
  const [hostProperties, setHostProperties] = useState([]);
  const [propsLoading, setPropsLoading] = useState(false);

  // PAN re-trigger dialog state
  const [showPanDialog, setShowPanDialog] = useState(false);
  const [panForm, setPanForm] = useState({ pan: '', name: '', dob: '' });
  const [panSubmitting, setPanSubmitting] = useState(false);

  const fetchHosts = useCallback(async () => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      toast.error('Admin session expired. Please login again.');
      return;
    }
    setLoading(true);
    try {
      const params = new URLSearchParams({ token });
      if (search.trim()) params.set('search', search.trim());
      if (panFilter !== 'all') params.set('pan_verified', panFilter === 'verified' ? 'yes' : 'no');
      const res = await axios.get(`${BACKEND_URL}/api/admin/hosts?${params.toString()}`);
      if (res.data.success) setHosts(res.data.data || []);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to load hosts';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [search, panFilter]);

  useEffect(() => {
    fetchHosts();
  }, [fetchHosts]);

  const handleViewProperties = async (host) => {
    setSelectedHost(host);
    setShowProperties(true);
    setHostProperties([]);
    setPropsLoading(true);
    try {
      // Reuse public properties endpoint with status=all by fetching pending + approved + rejected
      const statuses = ['pending', 'approved', 'rejected'];
      const results = await Promise.all(
        statuses.map((s) =>
          axios.get(`${BACKEND_URL}/api/properties/?status=${s}&limit=500&page=1`).then((r) => r.data?.data || [])
        )
      );
      const all = results.flat().filter((p) => p.host_id === host.host_id);
      setHostProperties(all);
    } catch (err) {
      toast.error('Failed to load properties for this host');
    } finally {
      setPropsLoading(false);
    }
  };

  const openPanDialog = (host) => {
    setSelectedHost(host);
    setPanForm({ pan: '', name: host.name || '', dob: '' });
    setShowPanDialog(true);
  };

  const handleDobChange = (val) => {
    const digits = val.replace(/\D/g, '').slice(0, 8);
    let out = digits;
    if (digits.length > 4) out = `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
    else if (digits.length > 2) out = `${digits.slice(0, 2)}/${digits.slice(2)}`;
    setPanForm((f) => ({ ...f, dob: out }));
  };

  const submitPanVerification = async () => {
    const token = localStorage.getItem('adminToken');
    const pan = panForm.pan.trim().toUpperCase();
    if (!/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(pan)) {
      toast.error('PAN must be 10 chars: 5 letters + 4 digits + 1 letter');
      return;
    }
    if (!panForm.name.trim()) {
      toast.error('Name is required');
      return;
    }
    if (!/^\d{2}\/\d{2}\/\d{4}$/.test(panForm.dob)) {
      toast.error('Date of birth must be DD/MM/YYYY');
      return;
    }
    setPanSubmitting(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/verify-host-pan`, {
        token,
        host_id: selectedHost.host_id,
        pan,
        name: panForm.name.trim(),
        date_of_birth: panForm.dob.trim(),
      });
      if (res.data.success && res.data.verified) {
        toast.success('PAN verified successfully');
      } else {
        toast.error(`Verification failed — ${res.data.data?.remarks || 'unable to verify'}`);
      }
      setShowPanDialog(false);
      fetchHosts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'PAN verification failed');
    } finally {
      setPanSubmitting(false);
    }
  };

  const exportCSV = () => {
    if (!hosts.length) {
      toast.info('No hosts to export');
      return;
    }
    const header = [
      'Host ID', 'Name', 'Email', 'Phone', 'Email Verified', 'Has Password',
      'PAN Verified', 'PAN Masked', 'Joined Year', 'Created At', 'Properties Total',
      'Properties Pending', 'Properties Approved', 'Properties Rejected',
    ];
    const rows = hosts.map((h) => [
      h.host_id, h.name, h.email, h.phone,
      h.email_verified ? 'Yes' : 'No',
      h.has_password ? 'Yes' : 'No',
      h.pan_verified ? 'Yes' : 'No',
      h.pan_masked || '',
      h.joined_year || '',
      h.created_at || '',
      h.properties?.total || 0,
      h.properties?.pending || 0,
      h.properties?.approved || 0,
      h.properties?.rejected || 0,
    ]);
    const csv = [header, ...rows]
      .map((r) => r.map((c) => `"${String(c ?? '').replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `geostays-hosts-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
    toast.success(`Exported ${hosts.length} hosts`);
  };

  const formatDate = (d) => {
    if (!d) return '—';
    try {
      return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch {
      return String(d).slice(0, 10);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-hosts-tab">
      {/* Filter bar */}
      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
            <div className="md:col-span-6">
              <label className="text-sm font-medium text-gray-700 block mb-1">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search by name, email, or phone"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9"
                  data-testid="admin-hosts-search-input"
                />
              </div>
            </div>
            <div className="md:col-span-3">
              <label className="text-sm font-medium text-gray-700 block mb-1">PAN Status</label>
              <Select value={panFilter} onValueChange={setPanFilter}>
                <SelectTrigger data-testid="admin-hosts-pan-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="verified">PAN Verified</SelectItem>
                  <SelectItem value="not_verified">Not Verified</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-3 flex gap-2">
              <Button
                onClick={fetchHosts}
                variant="outline"
                className="flex-1"
                disabled={loading}
                data-testid="admin-hosts-refresh-btn"
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <Button
                onClick={exportCSV}
                className="flex-1 bg-[#7CB342] hover:bg-[#6da036] text-white"
                data-testid="admin-hosts-export-btn"
              >
                <Download className="h-4 w-4 mr-2" />
                CSV
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      {loading ? (
        <div className="text-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-[#7CB342] mx-auto" />
          <p className="mt-3 text-gray-600 text-sm">Loading hosts...</p>
        </div>
      ) : hosts.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <Users className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No hosts found</h3>
            <p className="text-gray-600 text-sm">Try adjusting your search or filter criteria.</p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="admin-hosts-table">
                <thead className="bg-gray-50 border-b">
                  <tr className="text-left text-gray-600">
                    <th className="px-4 py-3 font-medium">Host</th>
                    <th className="px-4 py-3 font-medium">Contact</th>
                    <th className="px-4 py-3 font-medium">Status</th>
                    <th className="px-4 py-3 font-medium">Properties</th>
                    <th className="px-4 py-3 font-medium">Joined</th>
                    <th className="px-4 py-3 font-medium text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {hosts.map((h) => (
                    <tr key={h.host_id} className="border-b hover:bg-gray-50/60" data-testid={`admin-hosts-row-${h.host_id}`}>
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900">{h.name || '—'}</div>
                        <div className="text-xs text-gray-400 font-mono">{h.host_id?.slice(0, 12)}…</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center text-gray-700">
                          <Mail className="h-3.5 w-3.5 mr-1.5 text-gray-400" />
                          <span className="truncate max-w-[200px]">{h.email}</span>
                        </div>
                        <div className="flex items-center text-gray-500 text-xs mt-1">
                          <Phone className="h-3 w-3 mr-1.5 text-gray-400" />
                          {h.phone || '—'}
                        </div>
                      </td>
                      <td className="px-4 py-3 space-y-1">
                        <div className="flex flex-wrap gap-1">
                          {h.email_verified ? (
                            <Badge className="bg-green-100 text-green-700 hover:bg-green-100 text-[10px] px-1.5 py-0">
                              <Mail className="h-2.5 w-2.5 mr-1" /> Email
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-[10px] px-1.5 py-0 text-orange-600 border-orange-200">
                              Unverified
                            </Badge>
                          )}
                          {h.pan_verified ? (
                            <Badge className="bg-green-100 text-green-700 hover:bg-green-100 text-[10px] px-1.5 py-0">
                              <ShieldCheck className="h-2.5 w-2.5 mr-1" /> PAN
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-[10px] px-1.5 py-0 text-gray-500">
                              <ShieldAlert className="h-2.5 w-2.5 mr-1" /> No PAN
                            </Badge>
                          )}
                          {h.has_password ? (
                            <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100 text-[10px] px-1.5 py-0">
                              <KeyRound className="h-2.5 w-2.5 mr-1" /> Password
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-[10px] px-1.5 py-0 text-gray-500">
                              OTP-only
                            </Badge>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-700">
                        <div className="font-medium">{h.properties?.total || 0}</div>
                        <div className="text-[11px] text-gray-500">
                          {h.properties?.approved || 0} approved · {h.properties?.pending || 0} pending
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-600">{formatDate(h.created_at)}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setSelectedHost(h)}
                            className="h-8 px-2"
                            data-testid={`admin-host-view-${h.host_id}`}
                            title="View profile"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleViewProperties(h)}
                            className="h-8 px-2"
                            data-testid={`admin-host-properties-${h.host_id}`}
                            title="View properties"
                          >
                            <Building2 className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openPanDialog(h)}
                            className="h-8 px-2 text-[#7CB342]"
                            data-testid={`admin-host-pan-${h.host_id}`}
                            title="Trigger PAN verification"
                          >
                            <KeyIcon className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Profile dialog */}
      <Dialog
        open={!!selectedHost && !showProperties && !showPanDialog}
        onOpenChange={(o) => !o && setSelectedHost(null)}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Host Profile</DialogTitle>
            <DialogDescription>Read-only view of host account</DialogDescription>
          </DialogHeader>
          {selectedHost && (
            <div className="space-y-3 text-sm">
              <div><span className="text-gray-500">Name:</span> <span className="font-medium">{selectedHost.name || '—'}</span></div>
              <div><span className="text-gray-500">Email:</span> {selectedHost.email}</div>
              <div><span className="text-gray-500">Phone:</span> {selectedHost.phone || '—'}</div>
              <div><span className="text-gray-500">Host ID:</span> <span className="font-mono text-xs">{selectedHost.host_id}</span></div>
              <div><span className="text-gray-500">Joined:</span> {formatDate(selectedHost.created_at)}</div>
              <div className="flex flex-wrap gap-2 pt-2">
                {selectedHost.email_verified && <Badge className="bg-green-100 text-green-700">Email Verified</Badge>}
                {selectedHost.has_password && <Badge className="bg-blue-100 text-blue-700">Password Set</Badge>}
                {selectedHost.pan_verified && <Badge className="bg-green-100 text-green-700">PAN Verified ({selectedHost.pan_masked})</Badge>}
                {selectedHost.is_superhost && <Badge className="bg-amber-100 text-amber-700">Superhost</Badge>}
              </div>
              <div className="border-t pt-3">
                <div className="text-xs text-gray-500 mb-1">Properties</div>
                <div className="text-sm">
                  {selectedHost.properties?.total || 0} total — {selectedHost.properties?.approved || 0} approved,{' '}
                  {selectedHost.properties?.pending || 0} pending, {selectedHost.properties?.rejected || 0} rejected
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Properties drawer dialog */}
      <Dialog open={showProperties} onOpenChange={(o) => { if (!o) { setShowProperties(false); setSelectedHost(null); } }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Properties — {selectedHost?.name}</DialogTitle>
            <DialogDescription>{selectedHost?.email}</DialogDescription>
          </DialogHeader>
          {propsLoading ? (
            <div className="text-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-[#7CB342] mx-auto" />
            </div>
          ) : hostProperties.length === 0 ? (
            <p className="text-center text-sm text-gray-500 py-8">This host has no properties yet.</p>
          ) : (
            <div className="max-h-[60vh] overflow-y-auto divide-y">
              {hostProperties.map((p) => (
                <div key={p._id || p.id} className="py-3 flex items-start gap-3" data-testid={`admin-host-prop-${p._id || p.id}`}>
                  <img
                    src={p.images?.[0] || 'https://images.unsplash.com/photo-1518780664697-55e3ad937233?w=80'}
                    alt={p.title}
                    className="w-16 h-16 object-cover rounded"
                  />
                  <div className="flex-1">
                    <div className="font-medium text-gray-900">{p.title}</div>
                    <div className="text-xs text-gray-500">{p.location} · ₹{p.price?.toLocaleString?.()}</div>
                    <div className="mt-1">
                      <Badge
                        className={
                          p.status === 'approved'
                            ? 'bg-green-100 text-green-700'
                            : p.status === 'rejected'
                            ? 'bg-red-100 text-red-700'
                            : 'bg-yellow-100 text-yellow-700'
                        }
                      >
                        {p.status}
                      </Badge>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* PAN verification dialog */}
      <Dialog open={showPanDialog} onOpenChange={(o) => !o && setShowPanDialog(false)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Trigger PAN Verification</DialogTitle>
            <DialogDescription>
              Verify PAN for {selectedHost?.name} on behalf of the host via Sandbox.co.in.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium">Full Name (as on PAN)</label>
              <Input
                value={panForm.name}
                onChange={(e) => setPanForm({ ...panForm, name: e.target.value })}
                className="mt-1"
                data-testid="admin-pan-name-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium">PAN Number</label>
              <Input
                value={panForm.pan}
                onChange={(e) => setPanForm({ ...panForm, pan: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 10) })}
                placeholder="ABCDE1234F"
                maxLength={10}
                className="mt-1 uppercase font-mono"
                data-testid="admin-pan-number-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Date of Birth (DD/MM/YYYY)</label>
              <Input
                value={panForm.dob}
                onChange={(e) => handleDobChange(e.target.value)}
                placeholder="01/01/1990"
                inputMode="numeric"
                maxLength={10}
                className="mt-1"
                data-testid="admin-pan-dob-input"
              />
            </div>
            <Button
              onClick={submitPanVerification}
              disabled={panSubmitting}
              className="w-full bg-[#7CB342] hover:bg-[#6da036] text-white"
              data-testid="admin-pan-submit"
            >
              {panSubmitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Verifying...</> : 'Verify PAN'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminHostsTab;
